/* Timestamp formatting helpers that honor the user's time zone and
 * 12/24-hour preferences from the AppSettings context.
 *
 * Every helper accepts an optional `settings` object: `{ timeZone,
 * timeFormat }`. When omitted, we fall back to the snapshot stored in
 * localStorage so this file can be used both inside and outside React.
 */

import { readAppSettingsSnapshot } from '../hooks/useAppSettings'

const toDate = (input) => {
  if (!input) return null
  if (input instanceof Date) return isNaN(input.getTime()) ? null : input
  if (typeof input?.toDate === 'function') {
    try { return input.toDate() } catch { return null }
  }
  if (typeof input === 'number' || typeof input === 'string') {
    const d = new Date(input)
    return isNaN(d.getTime()) ? null : d
  }
  if (typeof input?.seconds === 'number') {
    return new Date(input.seconds * 1000)
  }
  return null
}

const resolveSettings = (settings) => {
  if (settings && (settings.timeZone || settings.timeFormat)) return settings
  return readAppSettingsSnapshot()
}

const baseOptions = (settings) => {
  const s = resolveSettings(settings)
  return {
    timeZone: s.timeZone || undefined,
    hour12: s.timeFormat !== '24h',
  }
}

export function formatDateTime(input, settings, overrides = {}) {
  const date = toDate(input)
  if (!date) return ''
  const opts = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    ...baseOptions(settings),
    ...overrides,
  }
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(date)
  } catch {
    return date.toLocaleString()
  }
}

export function formatDate(input, settings, overrides = {}) {
  const date = toDate(input)
  if (!date) return ''
  const { hour12, timeZone } = baseOptions(settings)
  const opts = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone,
    ...overrides,
  }
  // hour12 only matters when hour fields are included
  if (opts.hour || opts.minute) opts.hour12 = hour12
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(date)
  } catch {
    return date.toLocaleDateString()
  }
}

export function formatTime(input, settings, overrides = {}) {
  const date = toDate(input)
  if (!date) return ''
  const opts = {
    hour: 'numeric',
    minute: '2-digit',
    ...baseOptions(settings),
    ...overrides,
  }
  try {
    return new Intl.DateTimeFormat(undefined, opts).format(date)
  } catch {
    return date.toLocaleTimeString()
  }
}

export function formatRelativeTime(input, settings) {
  const date = toDate(input)
  if (!date) return ''
  const now = Date.now()
  const diff = now - date.getTime()
  const minute = 60 * 1000
  const hour = 60 * minute
  const day = 24 * hour
  const week = 7 * day
  if (diff < minute) return 'just now'
  if (diff < hour) {
    const m = Math.floor(diff / minute)
    return `${m}m ago`
  }
  if (diff < day) {
    const h = Math.floor(diff / hour)
    return `${h}h ago`
  }
  if (diff < week) {
    const d = Math.floor(diff / day)
    return `${d}d ago`
  }
  // Older than 7 days — fall back to a date.
  return formatDate(date, settings, { month: 'short', day: 'numeric', year: 'numeric' })
}

/* Convenience: Messages-style compact label — within 24h shows a
 * time, within a week shows a weekday, older shows a short date. */
export function formatMessageTimestamp(input, settings) {
  const date = toDate(input)
  if (!date) return ''
  const diff = Date.now() - date.getTime()
  if (diff < 86400000) return formatTime(date, settings)
  if (diff < 604800000) return formatDate(date, settings, { weekday: 'short', month: undefined, day: undefined, year: undefined })
  return formatDate(date, settings, { month: 'short', day: 'numeric', year: undefined })
}
